import {FC, useEffect, useState} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {Link, useHistory} from 'react-router-dom';
import {get} from 'lodash';
import {Bubble, Button, ConfirmProps, Icon, withConfirm} from '@deviceinsight/ng-ui-basic-components';
import {
	ContentHeader,
	messageBoxError,
	messageBoxSuccess,
	PaginationSettings,
	Reactable
} from '@deviceinsight/ng-ui-components';
import {Asset, Pageable, SortDirection, User} from '@deviceinsight/ng-ui-api-client';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTrashAlt} from '@fortawesome/free-solid-svg-icons';

import {fetchUsersByAccountNr} from '../../api/accountsApi';
import {Migration} from '../../modules/assetManagement/types';
import {listMigrations, deleteMigration, deleteDevice} from '../../api/assetManagementApi';
import formatTimestamp from '../common/formatTimestamp';
import RuleAsset from '../ruleengine/assetselection/RuleAsset';
import hasAuthority from '../common/hasAuthority';
import {DELETE_DEVICE} from '../../modules/common/consts';

import './AssetManagementPage.less';

type Props = ConfirmProps;

const AssetManagementPage: FC<Props> = ({confirm}) => {
	const INITIAL_PAGINATION_SETTINGS: PaginationSettings = {
		page: 0,
		size: 15,
		sort: {
			property: 'createdAt',
			direction: 'DESC'
		}
	};

	const DEVICE_MIGRATION_HEADER = {
		id: 'assetmgmt.devicemigration.title',
		defaultValue: 'Device Migration'
	};
	const DELETE_MIGRATION_CONFIRMATION_TITLE = {
		id: 'assetmgmt.deviceMigration.deleteMigrationConfirmTitle',
		defaultValue: 'Delete Migration'
	};
	const DELETE_MIGRATION_CONFIRMATION_BODY = {
		id: 'assetmgmt.deviceMigration.deleteMigrationConfirmBody',
		defaultValue: 'Are you sure you want to delete this Migration?'
	};
	const DELETE_DEVICE_CONFIRMATION_TITLE = {
		id: 'assetmgmt.deviceMigration.deleteDeviceConfirmTitle',
		defaultValue: 'Delete Device'
	};
	const DELETE_DEVICE_CONFIRMATION_BODY = {
		id: 'assetmgmt.deviceMigration.deleteDeviceConfirmBody',
		defaultValue:
			'Attention: This Asset will be removed. Within the next 60 days, you have the option of re-onboarding to reactivate the device. After that, the device will no longer be usable.'
	};
	const CREATE_MIGRATION_BUTTON_TOOLTIP = (
		<Trans i18nKey="assetmgmt.devicemigration.createMigrationTooltip">Create Migration</Trans>
	);

	const canSeeDeleteAssetSection = hasAuthority(DELETE_DEVICE);

	const removeMigration = (migrationId: string) => {
		confirm(DELETE_MIGRATION_CONFIRMATION_TITLE, DELETE_MIGRATION_CONFIRMATION_BODY).then(() => {
			setLoading(true);
			deleteMigration(migrationId)
				.then(() => {
					messageBoxSuccess(
						<Trans i18nKey="assetmgmt.deviceMigration.userDeletionSuccess">
							Migration was successfully deleted
						</Trans>
					);
					loadMigrations();
				})
				.catch(() =>
					messageBoxError(
						<Trans i18nKey="assetmgmt.deviceMigration.userDeletionError">Migration was not deleted</Trans>
					)
				)
				.finally(() => setLoading(false));
		});
	};

	const removeDevice = (device: Asset | undefined) => {
		if (device !== undefined) {
			confirm(DELETE_DEVICE_CONFIRMATION_TITLE, DELETE_DEVICE_CONFIRMATION_BODY).then(() => {
				setLoading(true);
				deleteDevice(device.urn)
					.then(() => {
						messageBoxSuccess(
							<Trans i18nKey="assetmgmt.deviceMigration.deviceDeletionSuccess" values={{device}}>
								{`Asset ${device?.name} was successfully deleted`}
							</Trans>
						);
						setAssetToDelete(undefined);
						history.push('/portaladmin/assetmanagement');
					})
					.catch(() =>
						messageBoxError(
							<Trans
								i18nKey="assetmgmt.deviceMigration.deviceDeletionError"
								values={{device}}>{`Asset ${device?.name} could not be deleted!`}</Trans>
						)
					)
					.finally(() => setLoading(false));
			});
		}
	};

	const loadMigrations = () => {
		setLoading(true);
		let migrations: Pageable<Migration>;

		listMigrations(paginationSettings)
			.then((migs) => {
				migrations = migs;
				const createdByList = migs.content.map((mig) => mig.createdBy);
				const distinctCreatedByList = createdByList.filter((n, i) => createdByList.indexOf(n) === i);
				return Promise.resolve(fetchUsersByAccountNr(distinctCreatedByList));
			})
			.then((users) => {
				migrations.content.forEach((mig) => {
					const currentUser = users.find((user: User | void) => user?.number === mig.createdBy);
					if (currentUser) {
						mig.createdBy = get(currentUser, 'principals.EMAIL[0].name') || '-';
					}
				});
				setPageable(migrations);
			})
			.catch((error) => {
				setPageable(undefined);
				console.error('Migration list could not be loaded: ', error);
			})
			.finally(() => setLoading(false));
	};

	const columnDefs = [
		{
			name: 'sourceAsset.name',
			label: {
				id: 'assetmgmt.columns.sourceAssetName',
				defaultValue: 'Source Asset'
			},
			sortable: false,
			render: (element: Migration) => {
				return (
					<Link
						to={`/assets/${element.sourceAsset.id}/dashboard`}
						data-testid={`sourceAsset-${element.sourceAsset.name}`}>
						{element.sourceAsset.name}
					</Link>
				);
			}
		},
		{
			name: 'targetAsset.name',
			label: {
				id: 'assetmgmt.columns.targetAssetName',
				defaultValue: 'Target Asset'
			},
			sortable: false,
			render: (element: Migration) => {
				return (
					<Link
						to={`/assets/${element.targetAsset.id}/dashboard`}
						data-testid={`targetAsset-${element.targetAsset.name}`}>
						{element.targetAsset.name}
					</Link>
				);
			}
		},
		{
			name: 'status',
			label: {
				id: 'assetmgmt.columns.status.label',
				defaultValue: 'Status'
			},
			sortable: true,
			render: ({status}: Migration) => {
				return (
					<div data-testid={`status-${status}`}>
						{status === 'PENDING' && <Trans i18nKey="assetmgmt.columns.status.pending">{status}</Trans>}
						{status === 'IN_PROGRESS' && (
							<Trans i18nKey="assetmgmt.columns.status.inProgress">{status}</Trans>
						)}
						{status === 'COMPLETED' && <Trans i18nKey="assetmgmt.columns.status.completed">{status}</Trans>}
					</div>
				);
			}
		},
		{
			name: 'completedAt',
			label: {
				id: 'assetmgmt.columns.completedAt',
				defaultValue: 'Completed At'
			},
			sortable: true,
			render: (element: Migration) => {
				return (
					<div data-testid={`completedAt-${element.completedAt}`}>
						{element.completedAt !== null && <div>{formatTimestamp(element.completedAt)}</div>}
					</div>
				);
			}
		},
		{
			name: 'createdAt',
			label: {
				id: 'assetmgmt.columns.createdAt',
				defaultValue: 'Created At'
			},
			sortable: true,
			render: (element: Migration) => {
				return <div data-testid={`createdAt-${element.createdAt}`}>{formatTimestamp(element.createdAt)}</div>;
			}
		},
		{
			name: 'createdBy',
			label: {
				id: 'assetmgmt.columns.createdBy',
				defaultValue: 'Created By'
			},
			sortable: false,
			render: (element: Migration) => {
				return (
					<div data-testid={`createdBy-${element.createdBy}`}>
						{element.createdBy !== null && <div>{element.createdBy}</div>}
					</div>
				);
			}
		},
		{
			name: 'delete',
			label: {
				id: 'assetmgmt.columns.delete',
				defaultValue: 'Delete'
			},
			sortable: false,
			render: (element: Migration) => {
				return (
					<div data-testid={`delete-${element.id}`}>
						{element.status === 'PENDING' && (
							<Button onClick={() => removeMigration(element.id)} naked>
								<Icon del />
							</Button>
						)}
					</div>
				);
			}
		}
	];

	const [loading, setLoading] = useState(true);
	const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>(INITIAL_PAGINATION_SETTINGS);
	const [pageable, setPageable] = useState<Pageable<Migration>>();
	const [assetToDelete, setAssetToDelete] = useState<Asset | undefined>(undefined);
	const history = useHistory();

	const createMigrationPath = `/portaladmin/assetmanagement/create`;

	useEffect(() => {
		loadMigrations();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationSettings]);

	return (
		<div className="asset-mgmt">
			<ContentHeader title={DEVICE_MIGRATION_HEADER}>
				<div className="di header-toolbar">
					<Bubble direction="bottom" content={CREATE_MIGRATION_BUTTON_TOOLTIP}>
						<Button
							className="tool add"
							style={{border: 0, marginRight: 7}}
							onClick={() => {
								history.push(createMigrationPath);
							}}
							data-testid="addMigrationButton"
						/>
					</Bubble>
				</div>
			</ContentHeader>
			<h2 className="di header dividing"></h2>
			<div style={{marginTop: 40}}>
				<Reactable
					columnDefs={columnDefs}
					isLoading={loading}
					onChangePage={(page) => setPaginationSettings({...paginationSettings, page})}
					onSort={(property: string, direction: SortDirection) =>
						setPaginationSettings({
							...paginationSettings,
							sort: {
								property,
								direction
							}
						})
					}
					pageable={pageable}
					uniqueItemKey={(migration: Migration) => migration.id}
					paginationSettings={paginationSettings}></Reactable>
			</div>

			{canSeeDeleteAssetSection ? (
				<div className="delete-device">
					<h2 className="di header dividing">
						<Trans i18nKey="assetmgmt.deleteasset.title">Delete Asset</Trans>
					</h2>
					<div className="asset-row">
						<div>
							<RuleAsset
								assetGroup={undefined}
								onAssetSelect={(asset) => setAssetToDelete(asset)}
								asset={assetToDelete}
								canEdit={true}
								data-testid="assetToDelete-button"
							/>
						</div>
						<Link
							className="asset-data"
							to={`/assets/${assetToDelete?.id}/dashboard`}
							data-testid={`assetToDelete-${assetToDelete?.urn}-urn`}>
							{assetToDelete?.urn}
						</Link>
					</div>
					<div data-testid={`delete-${assetToDelete?.id}`}>
						<Button
							primary
							style={{
								marginTop: '20px',
								width: '65px'
							}}
							onClick={() => removeDevice(assetToDelete)}
							disabled={assetToDelete === undefined}>
							<FontAwesomeIcon data-testid="delete-asset-button" icon={faTrashAlt} color="white" />
						</Button>
					</div>
				</div>
			) : undefined}
		</div>
	);
};

export default withConfirm(AssetManagementPage);
